/**
 * AppContext — pure utility helpers.
 *
 * Everything here is side-effect-free except for `storage` (which wraps
 * localStorage and never throws). No React, no Supabase. Splitting these
 * out keeps the provider file focused on orchestration.
 */

import { notifyStorageWriteFailed } from "@/lib/storageQuota";

/**
 * Coerces any thrown value into a displayable string.
 *
 * Supabase, `fetch`, and our own RPCs all throw different shapes — this
 * is the single place the provider calls for a user-facing message.
 */
export function getErrorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === "string") return err;
  return "An unexpected error occurred.";
}

/**
 * localStorage wrapper that never throws.
 *
 * Required because localStorage access throws in Safari private mode,
 * when storage quotas are exceeded, and during SSR. Swallowing errors
 * here means feature code can treat storage as best-effort without
 * littering `try`/`catch` everywhere.
 */
export const storage = {
  get: (key: string): string | null => {
    try { return localStorage.getItem(key); }
    catch { return null; }
  },
  set: (key: string, value: string): void => {
    try { localStorage.setItem(key, value); }
    catch { notifyStorageWriteFailed(); }
  },
  remove: (key: string): void => {
    try { localStorage.removeItem(key); }
    catch { /* ignore */ }
  },
};

/**
 * Minimal RFC 5545 VEVENT parser for the ICS holiday subscription.
 * Implementation lives in `src/lib/icsParser.ts` (shared with web worker).
 */
export { parseIcsDates } from "@/lib/icsParser";

// Legacy inline implementation removed — see icsParser.ts
